import React from 'react';
import { Table, Button, Tag, Input, App, Card, Dropdown, Modal } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  SearchOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { canChangeUserStatus, isUserDisabled } from '../../utils/userStatusRules';

const { Search } = Input;

const UserManagementTable = ({
  users,
  loading,
  currentUserEmail,
  currentUser,
  tenants,
  onEdit,
  onResetPassword,
  onDelete,
  onSetStatus
}) => {
  const { message } = App.useApp();
  const [searchText, setSearchText] = React.useState('');

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'purple';
      case 'tenant_admin':
        return 'blue';
      default:
        return 'green';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'tenant_admin':
        return 'Tenant Admin';
      default:
        return 'User';
    }
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'N/A';
    const tenant = tenants?.find(t => t.id === tenantId);
    return tenant ? tenant.name : tenantId;
  };

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
      width: 120,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      sorter: (a, b) => a.last_name.localeCompare(b.last_name),
      width: 120,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      // No fixed width: Email takes the remaining space and truncates (ellipsis).
      ellipsis: true,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleLabel(role)}
        </Tag>
      ),
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Tenant Admin', value: 'tenant_admin' },
        { text: 'User', value: 'user' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 150,
      render: (tenantId) => getTenantName(tenantId),
    },
    {
      // REQ-005 FE-5-6: `disabled` is additive in GET /admin/users, false if absent
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (isUserDisabled(record)
        ? <Tag color="red">Disabled</Tag>
        : <Tag>Active</Tag>),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Disabled', value: 'disabled' },
      ],
      onFilter: (value, record) => (value === 'disabled') === isUserDisabled(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => {
        const isCurrentUser = record.email === currentUserEmail;
        const isDisabled = isUserDisabled(record);
        // Same role rules as the backend: never your own row, tenant admins only
        // on non-admin users in their own tenant (CONTRACTS.md, REQ-005 Phase E).
        const canChangeStatus = Boolean(onSetStatus)
          && canChangeUserStatus(currentUser || { email: currentUserEmail }, record);

        const menuItems = [
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            disabled: isCurrentUser,
            onClick: () => onEdit(record)
          },
          {
            key: 'reset',
            label: 'Reset Password',
            icon: <LockOutlined />,
            disabled: isCurrentUser,
            onClick: () => {
              onResetPassword(record.email);
              message.info(`Password reset email will be sent to ${record.email}`);
            }
          },
          {
            key: 'status',
            label: isDisabled ? 'Enable' : 'Disable',
            icon: isDisabled ? <CheckCircleOutlined /> : <StopOutlined />,
            disabled: !canChangeStatus,
            onClick: () => {
              if (isDisabled) {
                onSetStatus(record.email, false);
                return;
              }
              Modal.confirm({
                title: 'Disable User',
                icon: <ExclamationCircleOutlined />,
                content: `Disable ${record.first_name} ${record.last_name}? They lose access immediately and cannot log in until an admin enables them again.`,
                okText: 'Yes, Disable',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => onSetStatus(record.email, true)
              });
            }
          },
          {
            type: 'divider'
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            disabled: isCurrentUser,
            onClick: () => {
              Modal.confirm({
                title: 'Delete User',
                icon: <ExclamationCircleOutlined />,
                content: `Are you sure you want to delete ${record.first_name} ${record.last_name}?`,
                okText: 'Yes, Delete',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => onDelete(record.email)
              });
            }
          }
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            disabled={isCurrentUser && menuItems.every(item => item.disabled)}
          >
            <Button
              icon={<MoreOutlined />}
              size="small"
            >
              Actions
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search by name or email"
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredUsers}
        loading={loading}
        rowKey="email"
        // FE-5-12: the columns fit the card at 1280 with the sidebar open; below that the
        // table scrolls inside itself instead of scrolling (and shifting) the whole page.
        scroll={{ x: 900 }}
        pagination={{
          total: filteredUsers.length,
          showTotal: (total) => `Total ${total} users`,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          defaultPageSize: 25,
        }}
        size="middle"
      />
    </Card>
  );
};

export default UserManagementTable;
